import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Puzzle, Plus, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatModulePrices } from '@/components/schools/V2SubscriptionForm';
import { EditableCardActions } from './EditableCardActions';
import { updateV2Subscription, type UpdateV2SubscriptionData } from '@/api/schools';
import type { SchoolEditData, SchoolOptions } from '@/types/school';

interface V2SubscriptionCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
}

export function V2SubscriptionCard({ school, options, onSaved }: V2SubscriptionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(
    school.v2Subscription?.offer?.id || null
  );
  const [offerBillingStartDate, setOfferBillingStartDate] = useState(
    school.v2Subscription?.offerBillingStartDate || ''
  );
  const [offerBillingEndDate, setOfferBillingEndDate] = useState(
    school.v2Subscription?.offerBillingEndDate || ''
  );
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>(
    school.v2Subscription?.moduleIds || []
  );
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    Array<{
      id?: number;
      productOptionId: number;
      billingStartDate: string;
      billingEndDate: string;
    }>
  >(
    school.v2Subscription?.options.map((opt) => ({
      id: opt.id,
      productOptionId: opt.productOptionId,
      billingStartDate: opt.billingStartDate ?? '',
      billingEndDate: opt.billingEndDate ?? '',
    })) || []
  );

  const startEditing = useCallback(() => {
    const sub = school.v2Subscription;
    setSelectedOfferId(sub?.offer?.id || null);
    setOfferBillingStartDate(sub?.offerBillingStartDate || '');
    setOfferBillingEndDate(sub?.offerBillingEndDate || '');
    setSelectedModuleIds(sub?.moduleIds || []);
    setSubscriptionOptions(
      sub?.options.map((opt) => ({
        id: opt.id,
        productOptionId: opt.productOptionId,
        billingStartDate: opt.billingStartDate ?? '',
        billingEndDate: opt.billingEndDate ?? '',
      })) || []
    );
    setError(null);
    setIsEditing(true);
  }, [school.v2Subscription]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const save = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);

      const data: UpdateV2SubscriptionData = {
        offerId: selectedOfferId,
        offerBillingStartDate: offerBillingStartDate || null,
        offerBillingEndDate: offerBillingEndDate || null,
        moduleIds: selectedModuleIds,
        options: subscriptionOptions
          .filter((opt) => opt.productOptionId > 0)
          .map((opt) => ({
            id: opt.id,
            productOptionId: opt.productOptionId,
            billingStartDate: opt.billingStartDate,
            billingEndDate: opt.billingEndDate || null,
          })),
      };

      await updateV2Subscription(school.code, data);
      setIsEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la mise a jour de l'abonnement");
    } finally {
      setIsSaving(false);
    }
  }, [selectedOfferId, offerBillingStartDate, offerBillingEndDate, selectedModuleIds, subscriptionOptions, school.code, onSaved]);

  const handleAddOption = () => {
    const today = new Date().toISOString().split('T')[0];
    setSubscriptionOptions((prev) => [
      ...prev,
      { productOptionId: 0, billingStartDate: today, billingEndDate: '' },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    setSubscriptionOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOptionChange = (
    index: number,
    field: 'productOptionId' | 'billingStartDate' | 'billingEndDate',
    value: string | number
  ) => {
    setSubscriptionOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    );
  };

  const getAvailableProductOptions = (excludeCurrentIndex?: number) => {
    if (!options.offers || !selectedOfferId) return [];
    const selectedOffer = options.offers.find((o) => o.id === selectedOfferId);
    if (!selectedOffer) return [];

    const excludeId = excludeCurrentIndex !== undefined
      ? subscriptionOptions[excludeCurrentIndex]?.productOptionId
      : undefined;

    const selectedOptionIds = subscriptionOptions
      .map((opt) => opt.productOptionId)
      .filter((id) => id !== excludeId && id > 0);

    return (options.productOptions || []).filter(
      (po) => po.productCode === selectedOffer.productCode && !selectedOptionIds.includes(po.id)
    );
  };

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Puzzle />}
          iconColor="green"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Offre V2</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {school.v2Subscription?.offer ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Produit</p>
                  <p className="font-medium">{school.v2Subscription.offer.productName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Offre</p>
                  <p className="font-medium">{school.v2Subscription.offer.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Debut facturation</p>
                  <p>
                    {school.v2Subscription.offerBillingStartDate
                      ? new Date(school.v2Subscription.offerBillingStartDate).toLocaleDateString('fr-FR')
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Fin facturation</p>
                  <p>
                    {school.v2Subscription.offerBillingEndDate
                      ? new Date(school.v2Subscription.offerBillingEndDate).toLocaleDateString('fr-FR')
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Modules inclus</p>
                <div className="flex flex-wrap gap-2">
                  {options.offers
                    .find((o) => o.id === school.v2Subscription?.offer?.id)
                    ?.modules.filter((m) => m.isDefault)
                    .map((module) => (
                      <Badge key={module.id} variant="secondary">
                        {module.name}
                      </Badge>
                    )) || (
                    <span className="text-muted-foreground text-sm">Aucun module</span>
                  )}
                </div>
              </div>

              {(() => {
                const offer = options.offers.find(
                  (o) => o.id === school.v2Subscription?.offer?.id
                );
                const selectedOptional = offer?.modules.filter(
                  (m) => !m.isDefault && school.v2Subscription?.moduleIds?.includes(m.id)
                );
                if (!selectedOptional || selectedOptional.length === 0) return null;
                return (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Modules complémentaires
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedOptional.map((module) => (
                        <Badge key={module.id} variant="outline">
                          {module.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {school.v2Subscription.options.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Options souscrites</p>
                  <div className="space-y-2">
                    {school.v2Subscription.options.map((option) => (
                      <div
                        key={option.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{option.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {option.type === 'recurring' ? 'Recurrent' : 'Ponctuel'}
                          </p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {option.billingStartDate && (
                            <p>Du {new Date(option.billingStartDate).toLocaleDateString('fr-FR')}</p>
                          )}
                          {option.billingEndDate && (
                            <p>Au {new Date(option.billingEndDate).toLocaleDateString('fr-FR')}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">Aucune offre selectionnee</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<Puzzle />}
        iconColor="green"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={save}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Offre V2</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="v2-offerId">Offre</Label>
            <Select
              value={selectedOfferId?.toString() || 'none'}
              onValueChange={(value) => {
                const newOfferId = value === 'none' ? null : parseInt(value);
                setSelectedOfferId(newOfferId);
                if (newOfferId && !offerBillingStartDate) {
                  setOfferBillingStartDate(new Date().toISOString().split('T')[0]);
                }
                if (newOfferId !== selectedOfferId) {
                  setSubscriptionOptions([]);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner une offre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune offre</SelectItem>
                {options.offers?.map((offer) => (
                  <SelectItem key={offer.id} value={offer.id.toString()}>
                    {offer.productName} - {offer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="v2-startDate">Debut facturation</Label>
            <Input
              id="v2-startDate"
              type="date"
              value={offerBillingStartDate}
              onChange={(e) => setOfferBillingStartDate(e.target.value)}
              disabled={!selectedOfferId}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="v2-endDate">Fin facturation</Label>
            <Input
              id="v2-endDate"
              type="date"
              value={offerBillingEndDate}
              onChange={(e) => setOfferBillingEndDate(e.target.value)}
              disabled={!selectedOfferId}
            />
          </div>
        </div>

        {selectedOfferId && (
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs">Modules inclus dans l'offre</Label>
            <div className="flex flex-wrap gap-2">
              {options.offers
                .find((o) => o.id === selectedOfferId)
                ?.modules.filter((m) => m.isDefault)
                .map((module) => (
                  <Badge key={module.id} variant="secondary">
                    {module.name}
                  </Badge>
                )) || <span className="text-muted-foreground text-sm">Aucun module</span>}
            </div>
          </div>
        )}

        {(() => {
          const offer = options.offers.find((o) => o.id === selectedOfferId);
          const optionalModules = offer?.modules.filter((m) => !m.isDefault) || [];
          if (optionalModules.length === 0) return null;
          return (
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">
                Modules complémentaires au choix (prix HT / élève / an, par tranche)
              </Label>
              <div className="grid grid-cols-1 gap-2 border rounded-md p-2">
                {optionalModules.map((module) => {
                  const prices = formatModulePrices(module);
                  return (
                    <div key={module.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`v2-sub-module-${module.id}`}
                        checked={selectedModuleIds.includes(module.id)}
                        onCheckedChange={() =>
                          setSelectedModuleIds((prev) =>
                            prev.includes(module.id)
                              ? prev.filter((id) => id !== module.id)
                              : [...prev, module.id]
                          )
                        }
                      />
                      <Label
                        htmlFor={`v2-sub-module-${module.id}`}
                        className="text-sm font-normal cursor-pointer flex items-center gap-2"
                      >
                        {module.name}
                        {prices && (
                          <span className="text-xs text-muted-foreground">{prices}</span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Options</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddOption}
              disabled={!selectedOfferId}
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter
            </Button>
          </div>

          {subscriptionOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune option</p>
          ) : (
            <div className="space-y-3">
              {subscriptionOptions.map((opt, index) => (
                <div key={index} className="flex items-end gap-3 p-3 bg-white rounded-lg border">
                  <div className="flex-1 space-y-2">
                    <Label className="text-xs">Option</Label>
                    <Select
                      value={opt.productOptionId?.toString() || ''}
                      onValueChange={(value) =>
                        handleOptionChange(index, 'productOptionId', parseInt(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableProductOptions(index).map((po) => (
                          <SelectItem key={po.id} value={po.id.toString()}>
                            {po.name} ({po.type === 'recurring' ? 'Recurrent' : 'Ponctuel'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32 space-y-2">
                    <Label className="text-xs">Debut</Label>
                    <Input
                      type="date"
                      value={opt.billingStartDate}
                      onChange={(e) => handleOptionChange(index, 'billingStartDate', e.target.value)}
                    />
                  </div>
                  <div className="w-32 space-y-2">
                    <Label className="text-xs">Fin</Label>
                    <Input
                      type="date"
                      value={opt.billingEndDate}
                      onChange={(e) => handleOptionChange(index, 'billingEndDate', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
